import type React from "react";
import { useEffect } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";

import { allTrackers } from "app/services/track/track";

import { Timeline } from "../Timeline/TimeLine";
import type { IPackagesPrice } from "../types";

import { TRACKING_EVENTS, TRANSLATION_KEYS } from "./constants";
import { getUSDPrice } from "./getPackagesData";
import { PackageItem } from "./PackageItem/PackageItem";
import {
    CloseButton,
    LeftContentContainer,
    RightContentContainer,
    Subtitle,
    Title,
} from "./StepPackagesStyledComponents";
import { ContentWrapper, TitleWrapper, Wrapper } from "./StyledComponents";
import { useStepPackages } from "./useStepPackages";

interface IStepPackagesCIAndSEODesktopProps {
    packages: IPackagesPrice;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    isSkipTrial: boolean;
}

export const StepPackagesCIAndSEODesktop: React.FC<IStepPackagesCIAndSEODesktopProps> = ({
    packages,
    setIsOpen,
    isSkipTrial,
}) => {
    const { translate } = useTranslate();
    const { currentCurrency, dueDate } = useStepPackages(packages);

    useEffect(() => {
        try {
            if (packages) {
                const { CompetitiveIntelligenceAnnually, CompetitiveIntelligenceSEOAnnually } = packages;
                const priceStrings = [
                    `CompetitiveIntelligence: ${getUSDPrice(CompetitiveIntelligenceAnnually)}`,
                    `CompetitiveIntelligenceSEO: ${getUSDPrice(CompetitiveIntelligenceSEOAnnually)}`,
                ];
                allTrackers.trackEvent(TRACKING_EVENTS.category, TRACKING_EVENTS.actionShow, priceStrings.join(", "));
            }
        } catch {
            // Prevent crashing
        }
    }, [packages]);

    const translatedTitle = isSkipTrial
        ? translate(TRANSLATION_KEYS.skipTrialTitle)
        : translate(TRANSLATION_KEYS.title);
    const translatedSubtitle = translate(TRANSLATION_KEYS.subtitle);

    return (
        <Wrapper>
            <ContentWrapper>
                <LeftContentContainer>
                    <CloseButton
                        iconName="clear"
                        iconSize="sm"
                        type="flat"
                        onClick={() => setIsOpen?.(false)}
                    />
                    <TitleWrapper>
                        <Title
                            fontVariant="boldH3"
                            color={colorsPalettes.carbon[500]}
                            title={translatedTitle}
                        >
                            {translatedTitle}
                        </Title>
                        <Subtitle
                            fontVariant="subtitle1"
                            color={colorsPalettes.carbon[500]}
                            title={translatedSubtitle}
                        >
                            {translatedSubtitle}
                        </Subtitle>
                    </TitleWrapper>
                    <Timeline
                        dueDate={dueDate}
                        currency={currentCurrency}
                    />
                </LeftContentContainer>
                <RightContentContainer>
                    <PackageItem packages={packages} />
                </RightContentContainer>
            </ContentWrapper>
        </Wrapper>
    );
};
